import { apiFetch } from "@/lib/apiClient";
import {
  Category,
  Subcategory,
  Product,
  FpoMarketStock,
  ProductEnquiryPayload,
  ContactEnquiryPayload,
  IsraelAgritechEoiPayload,
  VendorStorefront,
} from "../types/public.types";

export const publicService = {
  async getCategories(): Promise<Category[]> {
    const res = await apiFetch("/categories");
    if (!res.ok) throw new Error("Failed to fetch categories");
    return res.json();
  },

  async getSubcategories(): Promise<Subcategory[]> {
    const res = await apiFetch("/subcategories");
    if (!res.ok) throw new Error("Failed to fetch subcategories");
    return res.json();
  },

  async getCategoryBySlug(slug: string): Promise<Category | null> {
    const res = await apiFetch(`/categories/slug/${slug}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to fetch category");
    return res.json();
  },

  async getSubcategoriesByCategoryId(categoryId: string): Promise<Subcategory[]> {
    const res = await apiFetch(`/subcategories/category/${categoryId}`);
    if (!res.ok) throw new Error("Failed to fetch subcategories for category");
    return res.json();
  },

  async getProducts(params?: {
    category?: string;
    subcategory?: string;
    page?: number;
    limit?: number;
    search?: string;
    vendorId?: string;
  }): Promise<{ products: Product[]; total: number; page: number; limit: number }> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.subcategory) queryParams.append("subcategory", params.subcategory);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.vendorId) queryParams.append("vendorId", params.vendorId);

    const res = await apiFetch(`/products?${queryParams.toString()}`);
    if (!res.ok) throw new Error("Failed to fetch products");
    return res.json();
  },

  async getSubcategoryBySlug(slug: string): Promise<Subcategory | null> {
    const res = await apiFetch(`/subcategories/slug/${slug}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to fetch subcategory");
    return res.json();
  },

  async getProductBySlug(slug: string): Promise<Product | null> {
    const res = await apiFetch(`/products/slug/${slug}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to fetch product details");
    return res.json();
  },

  async getServiceBySlug(slug: string): Promise<any | null> {
    const res = await apiFetch(`/services/slug/${slug}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to fetch service details");
    return res.json();
  },

  async getServices(params?: {
    category?: string;
    subcategory?: string;
    page?: number;
    limit?: number;
    search?: string;
    vendorId?: string;
  }): Promise<{ services: any[]; total: number; page: number; limit: number }> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.subcategory) queryParams.append("subcategory", params.subcategory);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.vendorId) queryParams.append("vendorId", params.vendorId);

    const res = await apiFetch(`/services?${queryParams.toString()}`);
    if (!res.ok) throw new Error("Failed to fetch services");
    return res.json();
  },

  async submitProductEnquiry(payload: ProductEnquiryPayload): Promise<unknown> {
    const res = await apiFetch("/product-enquiries", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to submit enquiry");
    }
    return res.json();
  },

  async submitServiceEnquiry(payload: any): Promise<unknown> {
    const res = await apiFetch("/service-enquiries", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to submit enquiry");
    }
    return res.json();
  },

  async submitContactEnquiry(payload: ContactEnquiryPayload): Promise<unknown> {
    const res = await apiFetch("/contact-enquiries", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error("Failed to submit enquiry. Please try again later.");
    }
    return res.json();
  },

  async getFpoMarketStocks(): Promise<FpoMarketStock[]> {
    const res = await apiFetch("/fpo-market");
    if (!res.ok) throw new Error("Failed to retrieve FPO profiles.");
    return res.json();
  },

  async getFpoMarketStocksByUserId(userId: string): Promise<FpoMarketStock[]> {
    const res = await apiFetch(`/fpo-market/user/${userId}`);
    if (!res.ok) throw new Error("Failed to fetch market listings for FPO user.");
    return res.json();
  },

  async getFpoMarketStockById(id: string): Promise<FpoMarketStock> {
    const res = await apiFetch(`/fpo-market/${id}`);
    if (!res.ok) throw new Error("FPO profile not found or has been removed.");
    return res.json();
  },

  async submitIsraelAgritechEoi(payload: IsraelAgritechEoiPayload): Promise<unknown> {
    const res = await apiFetch("/israel-agritech", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to submit expression of interest.");
    }
    return res.json();
  },

  async getVendorProfileBySlug(slug: string): Promise<VendorStorefront> {
    const res = await apiFetch(`/auth/vendors/slug/${slug}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error("Storefront not found");
      throw new Error("Failed to fetch storefront");
    }
    return res.json();
  },

  async getVendorProfile(vendorId: string): Promise<VendorStorefront> {
    const res = await apiFetch(`/auth/vendors/${vendorId}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error("Vendor storefront not found");
      throw new Error("Failed to fetch vendor storefront");
    }
    return res.json();
  },

  async getVendorProfileByEmail(email: string): Promise<VendorStorefront> {
    const res = await apiFetch(`/auth/vendors/email/${encodeURIComponent(email)}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error("Vendor storefront not found");
      throw new Error("Failed to fetch vendor storefront");
    }
    return res.json();
  },

  async getVendors(): Promise<VendorStorefront[]> {
    const res = await apiFetch("/auth/vendors");
    if (!res.ok) throw new Error("Failed to fetch vendors storefront list");
    return res.json();
  }
};
